import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Chip,
  Tabs,
  Tab,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Alert,
} from "@mui/material";
import { Add, ContentCopy, Delete } from "@mui/icons-material";
import { partnerDashboardService, PartnerDriver } from "../../../apis/partnerDashboard";

interface PartnerDriversProps {
  partnerId: string;
}

const PartnerDrivers: React.FC<PartnerDriversProps> = ({ partnerId }) => {
  const [drivers, setDrivers] = useState<PartnerDriver[]>([]);
  const [loading, setLoading] = useState(false);
  const [driverTypeTab, setDriverTypeTab] = useState(0); // 0: All, 1: Internal, 2: External
  const [openAddDriverModal, setOpenAddDriverModal] = useState(false);
  const [openAgencyCodeModal, setOpenAgencyCodeModal] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [selectedDriver, setSelectedDriver] = useState<PartnerDriver | null>(null);
  const [phone, setPhone] = useState("");
  const [agencyCode, setAgencyCode] = useState("");
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" as "success" | "error" });

  useEffect(() => {
    fetchDrivers();
    fetchAgencyCode();
  }, [partnerId, driverTypeTab]);

  const fetchDrivers = async () => {
    setLoading(true);
    try {
      let data: PartnerDriver[] = [];
      if (driverTypeTab === 0) {
        data = await partnerDashboardService.getPartnerDrivers(partnerId);
      } else if (driverTypeTab === 1) {
        data = await partnerDashboardService.getPartnerDrivers(partnerId, true);
      } else {
        data = await partnerDashboardService.getPartnerDrivers(partnerId, false);
      }
      console.log("Partner Drivers API Response:", data);
      setDrivers(Array.isArray(data) ? data : []);
    } catch (error: any) {
      console.error("Error fetching drivers:", error);
      setSnackbar({
        open: true,
        message: error.response?.data?.message || "Failed to fetch drivers",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchAgencyCode = async () => {
    try {
      const code = await partnerDashboardService.getAgencyCode(partnerId);
      console.log("Agency Code API Response:", code);
      setAgencyCode(code || "");
    } catch (error: any) {
      console.error("Failed to fetch agency code:", error);
      setSnackbar({
        open: true,
        message: error.response?.data?.message || "Failed to fetch agency code",
        severity: "error",
      });
    }
  };

  const handleAddInternalDriver = async () => {
    if (!phone) {
      setSnackbar({
        open: true,
        message: "Please enter a phone number",
        severity: "error",
      });
      return;
    }

    try {
      await partnerDashboardService.addInternalDriver(partnerId, phone);
      setSnackbar({
        open: true,
        message: "Driver invitation sent. Driver will be linked when they sign up with this phone number.",
        severity: "success",
      });
      setOpenAddDriverModal(false);
      setPhone("");
      fetchDrivers();
    } catch (error: any) {
      setSnackbar({
        open: true,
        message: error.response?.data?.message || "Failed to add driver",
        severity: "error",
      });
    }
  };

  const handleCopyAgencyCode = () => {
    navigator.clipboard.writeText(agencyCode);
    setSnackbar({
      open: true,
      message: "Agency code copied to clipboard",
      severity: "success",
    });
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setDriverTypeTab(newValue);
  };

  const handleDeleteDriver = (driver: PartnerDriver) => {
    setSelectedDriver(driver);
    setOpenDeleteDialog(true);
  };

  const confirmDeleteDriver = async () => {
    if (!selectedDriver) return;
    
    try {
      if (selectedDriver.isPending) {
        // Remove pending invitation by driver ID (pseudo ID) and phone number
        const phone = selectedDriver.phone;
        if (!phone) {
          setSnackbar({
            open: true,
            message: "Phone number is required to remove pending invitation",
            severity: "error",
          });
          return;
        }
        if (!selectedDriver._id) {
          setSnackbar({
            open: true,
            message: "Driver ID is required to remove pending invitation",
            severity: "error",
          });
          return;
        }
        await partnerDashboardService.removePendingInvitation(partnerId, selectedDriver._id, phone);
        setSnackbar({
          open: true,
          message: "Pending invitation removed successfully",
          severity: "success",
        });
      } else {
        // Remove linked internal driver by driver ID
        await partnerDashboardService.removeInternalDriver(partnerId, selectedDriver._id);
        setSnackbar({
          open: true,
          message: "Internal driver removed successfully",
          severity: "success",
        });
      }
      setOpenDeleteDialog(false);
      setSelectedDriver(null);
      fetchDrivers();
    } catch (error: any) {
      setSnackbar({
        open: true,
        message: error.response?.data?.message || "Failed to remove driver",
        severity: "error",
      });
    }
  };

  return (
    <Box sx={{ mt: 3 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
        <Typography variant="h5">Drivers</Typography>
        <Box>
          <Button
            variant="outlined"
            sx={{ mr: 2 }}
            onClick={() => setOpenAgencyCodeModal(true)}
            startIcon={<ContentCopy />}
          >
            Agency Code
          </Button>
          <Button variant="contained" color="primary" onClick={() => setOpenAddDriverModal(true)} startIcon={<Add />}>
            Add Internal Driver
          </Button>
        </Box>
      </Box>

      <Tabs value={driverTypeTab} onChange={handleTabChange} sx={{ mb: 2 }}>
        <Tab label="All Drivers" />
        <Tab label="Internal Drivers" />
        <Tab label="External Drivers" />
      </Tabs>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Phone</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  Loading...
                </TableCell>
              </TableRow>
            ) : drivers.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  No drivers found
                </TableCell>
              </TableRow>
            ) : (
              drivers.map((driver) => (
                <TableRow key={driver._id}>
                  <TableCell>
                    {driver.isPending 
                      ? "Pending Invitation" 
                      : `${driver.firstName || ""} ${driver.lastName || ""}`.trim() || "N/A"}
                  </TableCell>
                  <TableCell>{driver.email}</TableCell>
                  <TableCell>{driver.phone || "N/A"}</TableCell>
                  <TableCell>
                    <Chip
                      label={driver.isInternalDriver ? "Internal" : "External"}
                      color={driver.isInternalDriver ? "primary" : "secondary"}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={driver.vtcStatus || "invited"}
                      color={
                        driver.vtcStatus === "active" || driver.vtcStatus === "approved"
                          ? "success" 
                          : driver.vtcStatus === "invited" || driver.isPending
                          ? "warning"
                          : "default"
                      }
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    {driver.isInternalDriver && (
                      <Button
                        variant="outlined"
                        color="error"
                        size="small"
                        startIcon={<Delete />}
                        onClick={() => handleDeleteDriver(driver)}
                      >
                        Remove
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Add Internal Driver Modal */}
      <Dialog open={openAddDriverModal} onClose={() => setOpenAddDriverModal(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add Internal Driver</DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
            <TextField
              label="Driver Phone Number"
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              fullWidth
              required
              helperText="Driver will be automatically linked when they sign up with this phone number"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenAddDriverModal(false)}>Cancel</Button>
          <Button onClick={handleAddInternalDriver} variant="contained">
            Add Driver
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Driver Confirmation Dialog */}
      <Dialog open={openDeleteDialog} onClose={() => setOpenDeleteDialog(false)}>
        <DialogTitle>
          {selectedDriver?.isPending ? "Remove Pending Invitation" : "Remove Internal Driver"}
        </DialogTitle>
        <DialogContent>
          <Typography>
            {selectedDriver?.isPending
              ? "Are you sure you want to remove this pending invitation? The driver will not be automatically linked when they sign up."
              : "Are you sure you want to remove this internal driver? They will be unlinked from your partner account."}
          </Typography>
          {selectedDriver && (
            <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
              {selectedDriver.isPending
                ? `Phone: ${selectedDriver.phone}`
                : `Driver: ${selectedDriver.firstName} ${selectedDriver.lastName} (${selectedDriver.phone || selectedDriver.email})`}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDeleteDialog(false)}>Cancel</Button>
          <Button onClick={confirmDeleteDriver} variant="contained" color="error">
            Remove
          </Button>
        </DialogActions>
      </Dialog>

      {/* Agency Code Modal */}
      <Dialog open={openAgencyCodeModal} onClose={() => setOpenAgencyCodeModal(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Agency Code</DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
            <Typography variant="body2" color="textSecondary">
              Share this agency code with external drivers. They can use this code during registration to join your
              partner network.
            </Typography>
            <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
              <TextField
                value={agencyCode}
                fullWidth
                InputProps={{
                  readOnly: true,
                }}
              />
              <Button variant="outlined" onClick={handleCopyAgencyCode} startIcon={<ContentCopy />}>
                Copy
              </Button>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenAgencyCodeModal(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
};

export default PartnerDrivers;

